import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Grid, TextField, Button, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../common/PageHeader';
import presenceService from '../../services/presenceService';
import apiService from '../../services/apiServices';
import { formatHHMM } from '../presence/LivePresence';
import { printReport } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { ReportPage, ExportMenu } from './ReportKit';

function toYMD(d){ const y=d.getFullYear(); const m=('0'+(d.getMonth()+1)).slice(-2); const da=('0'+d.getDate()).slice(-2); return `${y}-${m}-${da}`; }

export default function PresenceLogs(){
  const { t } = useTranslation();
  const [from, setFrom] = useState(() => { const d=new Date(); return toYMD(d); });
  const [to, setTo] = useState(() => { const d=new Date(); return toYMD(d); });
  const [events, setEvents] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);

  const pdfCols = [
    { label: t('report.date'), key: 'day', align: 'left', width: '12%' },
    { label: t('report.worker'), key: 'name', align: 'left', width: '28%' },
    { label: t('report.checkIn'), key: 'checkInStr', align: 'left', width: '15%' },
    { label: t('report.duration'), key: 'breakMin', align: 'right', width: '12%' },
    { label: t('report.checkOut'), key: 'checkOutStr', align: 'left', width: '15%' },
    { label: t('report.totalHours'), key: 'workStr', align: 'right', width: '18%' },
  ];

  useEffect(()=>{ apiService().get('workers').then(r=> r?.success && setWorkers(r.data||[])); },[]);
  const load = useCallback(async () => {
    const r = await presenceService().getRange(from,to);
    setEvents(r||[]);
  }, [from, to]);
  useEffect(()=>{ load(); },[from, to, load]);

  const rows = useMemo(()=>{
    // Aggregate per day/worker
    const groups = new Map();
    const keyOf=(day,id)=> day+'__'+id;
    (events||[]).forEach(e=>{
      const at = new Date(e.at || e.At);
      const day = toYMD(at);
      const id = String(e.workerId || e.WorkerId || e.employeeId || e.EmployeeId || e.Employee || e.Worker);
      const k = keyOf(day,id);
      const g = groups.get(k) || { day, id, breaks: [] };
      switch ((e.type || e.Type || '').toLowerCase()){
        case 'checkin': g.checkIn = g.checkIn || at.toISOString(); break;
        case 'break_start': g.breaks.push({ start: at.toISOString() }); break;
        case 'break_end': {
          let i = g.breaks.length - 1; for (; i>=0; i--){ if (g.breaks[i] && !g.breaks[i].end){ g.breaks[i].end = at.toISOString(); break; } }
          break; }
        case 'checkout': g.checkOut = at.toISOString(); break;
        default: break;
      }
      groups.set(k,g);
    });
    return Array.from(groups.values());
  },[events]);

  const enriched = useMemo(()=> rows.map(r=>{
    const w = (workers||[]).find(x=> String(x.Id)===String(r.id));
    const name = (w? `${w.FirstName||''} ${w.LastName||''}`.trim() : r.id);
    const workMin = (()=>{
      if(!r.checkIn) return 0; const start=new Date(r.checkIn).getTime(); const end=r.checkOut? new Date(r.checkOut).getTime(): start; const total=Math.max(0, Math.round((end-start)/60000)); const breakMin=(r.breaks||[]).reduce((a,b)=>{ const st=new Date(b.start).getTime(); const en=b.end? new Date(b.end).getTime(): st; return a + Math.max(0, Math.round((en-st)/60000)); },0); return Math.max(0, total-breakMin);
    })();
    const breakMin=(r.breaks||[]).reduce((a,b)=>{ const st=new Date(b.start).getTime(); const en=b.end? new Date(b.end).getTime(): st; return a + Math.max(0, Math.round((en-st)/60000)); },0);
    return { ...r, name, workMin, breakMin };
  }),[rows, workers]);

  const pdfRows = enriched.map(r => ({
    day: r.day,
    name: r.name,
    checkInStr: r.checkIn ? new Date(r.checkIn).toLocaleTimeString().slice(0,5) : '—',
    breakMin: r.breakMin || 0,
    checkOutStr: r.checkOut ? new Date(r.checkOut).toLocaleTimeString().slice(0,5) : '—',
    workStr: formatHHMM(r.workMin),
  }));

  const exportPdf = () => printReport({
    title: t('reports.presenceLogs'),
    subtitle: `${from} → ${to}`,
    orientation: 'landscape',
    columns: pdfCols,
    rows: pdfRows,
  });

  function exportCsv(){
    const headers=[t('report.date'), t('report.worker'), t('report.checkIn'), t('report.duration'), t('report.checkOut'), t('report.totalHours')];
    const lines=[headers.join(',')];
    enriched.forEach(r=>{ const vals=[r.day, r.name, r.checkIn? new Date(r.checkIn).toLocaleTimeString().slice(0,5):'', String(r.breakMin||''), r.checkOut? new Date(r.checkOut).toLocaleTimeString().slice(0,5):'', formatHHMM(r.workMin) ].map(v=>'"'+String(v).replace(/"/g,'""')+'"'); lines.push(vals.join(',')); });
    const csv=lines.join('\r\n'); const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='presence_logs.csv'; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <ReportPage>
      <PageHeader title={t('reports.presenceLogs')} />
      <Card sx={{ mb:2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><TextField label={t('report.from')} type="date" value={from} onChange={(e)=>setFrom(e.target.value)} fullWidth InputLabelProps={{shrink:true}} /></Grid>
            <Grid item xs={12} sm={6} md={3}><TextField label={t('report.to')} type="date" value={to} onChange={(e)=>setTo(e.target.value)} fullWidth InputLabelProps={{shrink:true}} /></Grid>
            <Grid item xs={12} sm={6} md={2}><Button fullWidth variant="outlined" onClick={load} sx={{ height:'100%' }}>{t('report.load')}</Button></Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={exportCsv} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} /></Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('report.date')}</TableCell>
                <TableCell>{t('report.worker')}</TableCell>
                <TableCell>{t('report.checkIn')}</TableCell>
                <TableCell>{t('report.duration')}</TableCell>
                <TableCell>{t('report.checkOut')}</TableCell>
                <TableCell align="right">{t('report.totalHours')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enriched.map((r,idx)=> (
                <TableRow key={idx}>
                  <TableCell>{r.day}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.checkIn? new Date(r.checkIn).toLocaleTimeString().slice(0,5):''}</TableCell>
                  <TableCell>{r.breakMin||''}</TableCell>
                  <TableCell>{r.checkOut? new Date(r.checkOut).toLocaleTimeString().slice(0,5):''}</TableCell>
                  <TableCell align="right">{formatHHMM(r.workMin)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <SendReportEmailDialog
        open={emailOpen} onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.presenceLogs')}
        subtitle={`${from} → ${to}`}
        columns={pdfCols} rows={pdfRows}
        workers={workers}
        orientation="landscape"
      />
    </ReportPage>
  );
}
